import { doc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { normalizeBrazilianPhone } from "./passengerPhoneService";

const RESERVED_NAMES = new Set(["usuário", "usuario", "passageiro", "você", "voce"]);

export function normalizePassengerName(value) {
  return String(value || "").trim().replace(/\s+/g, " ");
}

export function isExplicitPassengerName(value) {
  const name = normalizePassengerName(value);
  return name.length >= 3 && name.split(" ").length >= 2
    && !RESERVED_NAMES.has(name.toLocaleLowerCase("pt-BR"));
}

export function isPassengerProfileCompleteForUser(user, profile) {
  if (!user?.uid || !profile || profile.perfilCompleto !== true) return false;
  if (profile.uid && profile.uid !== user.uid) return false;
  if (!isExplicitPassengerName(profile.nome)) return false;

  const authPhone = normalizeBrazilianPhone(user.phoneNumber);
  if (authPhone.valid) {
    const profilePhone = normalizeBrazilianPhone(profile.telefone);
    if (!profilePhone.valid || profilePhone.national !== authPhone.national) return false;
  }
  return true;
}

export function resolvePassengerRegistrationDefaults(user, profile = {}) {
  const verifiedProviderEmail = user?.email && user.emailVerified === true ? user.email : "";
  const providerIds = new Set((user?.providerData || []).map(provider => provider.providerId));
  const trustedFederatedProfile = providerIds.has("google.com") || providerIds.has("apple.com");
  const providerName = trustedFederatedProfile && isExplicitPassengerName(user?.displayName) ? user.displayName : "";
  return {
    name: normalizePassengerName(providerName || profile.nome || user?.displayName),
    email: String(verifiedProviderEmail || profile.emailContato || profile.email || "").trim().toLowerCase(),
    emailVerifiedByProvider: Boolean(verifiedProviderEmail),
    birthDate: String(profile.dataNascimento || ""),
    photoURL: String((trustedFederatedProfile && user?.photoURL) || profile.photoURL || user?.photoURL || ""),
    createdAt: profile.criadoEm || "",
    phone: String(profile.telefone || ""),
  };
}

export async function savePassengerProfile({
  user,
  name,
  email,
  birthDate,
  photoURL = "",
  existingProfile = {},
  database = db,
  getCurrentUid = () => user?.uid,
  now = () => new Date().toISOString(),
}) {
  if (!user?.uid || getCurrentUid() !== user.uid) {
    const error = new Error("A sessão mudou durante o cadastro. Entre novamente.");
    error.code = "profile/session-changed";
    throw error;
  }
  const normalizedName = normalizePassengerName(name);
  if (!isExplicitPassengerName(normalizedName)) {
    const error = new Error("Informe seu nome e sobrenome.");
    error.code = "profile/invalid-name";
    throw error;
  }

  const normalizedEmail = String(user.email && user.emailVerified ? user.email : email || "").trim().toLowerCase();
  if (!/^\S+@\S+\.\S+$/.test(normalizedEmail)) {
    const error = new Error("Informe um e-mail válido.");
    error.code = "profile/invalid-email";
    throw error;
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(birthDate || ""))) {
    const error = new Error("Informe sua data de nascimento.");
    error.code = "profile/invalid-birth-date";
    throw error;
  }

  const authPhone = normalizeBrazilianPhone(user.phoneNumber);
  const existingPhone = normalizeBrazilianPhone(existingProfile.telefone);
  const payload = {
    uid: user.uid,
    nome: normalizedName,
    email: normalizedEmail,
    dataNascimento: birthDate,
    photoURL,
    telefone: authPhone.valid ? authPhone.national : existingPhone.valid ? existingPhone.national : "",
    tipo: "passenger",
    criadoEm: existingProfile.criadoEm || now(),
    perfilCompleto: true,
  };
  await setDoc(doc(database, "users", user.uid), payload, { merge: true });
  return payload;
}
